import { useState, useEffect, useMemo } from 'react';
import { getImageUrl } from '@utils/imageUrl';
import { buildGenerationMessages } from '@data/generationStepMessages';
import GardenAssemblyAnimation from './GardenAssemblyAnimation';
import './PlanGenerationPuzzleAnimation.css';
import { useI18n } from '../i18n';

const COLS = 3;
const ROWS = 3;
const PUZZLE_PIECES = COLS * ROWS;
const MESSAGE_INTERVAL_MS = 2800;

interface PlanGenerationPuzzleAnimationProps {
	beforeImageUrl?: string | null;
	selectedComponents?: string[];
	subMessage?: string;
	/** One quick puzzle cycle for logged-out “try” flow (no long loop). */
	teaserMode?: boolean;
	onTeaserComplete?: () => void;
}

export default function PlanGenerationPuzzleAnimation({
	beforeImageUrl,
	selectedComponents,
	subMessage =
		'We’re preparing your garden design. This usually takes about 1–3 minutes. You can switch to another tab if you like—just keep this page open and we’ll keep working.',
	teaserMode = false,
	onTeaserComplete,
}: PlanGenerationPuzzleAnimationProps) {
	const { language } = useI18n();
	const messages = useMemo(
		() => buildGenerationMessages(selectedComponents),
		[selectedComponents]
	);
	const localizedMessages = useMemo(() => {
		if (language === 'en') return messages;
		const dict: Record<string, { de: string; hr: string }> = {
			'Analysing your space…': { de: 'Ihr Raum wird analysiert…', hr: 'Analiziramo vaš prostor…' },
			'Mapping your garden…': { de: 'Ihr Garten wird kartiert…', hr: 'Mapiramo vaš vrt…' },
			'Designing the layout…': { de: 'Das Layout wird gestaltet…', hr: 'Dizajniramo raspored…' },
			'Choosing the best spots…': { de: 'Die besten Positionen werden gewählt…', hr: 'Biramo najbolje pozicije…' },
			'Digging the pool…': { de: 'Der Poolbereich wird geplant…', hr: 'Planiramo prostor za bazen…' },
			'Tiling the pool…': { de: 'Poolbeläge werden geplant…', hr: 'Planiramo obloge bazena…' },
			'Planning the pool area…': { de: 'Der Poolbereich wird entworfen…', hr: 'Dizajniramo zonu bazena…' },
			'Planning the water feature…': { de: 'Das Wasserelement wird geplant…', hr: 'Planiramo vodeni element…' },
			'Placing fountain and pond…': { de: 'Brunnen und Teich werden positioniert…', hr: 'Pozicioniramo fontanu i jezerce…' },
			'Planning the hot tub area…': { de: 'Der Whirlpool-Bereich wird geplant…', hr: 'Planiramo zonu hidromasažne kade…' },
			'Laying out the vegetable garden…': { de: 'Der Gemüsegarten wird angelegt…', hr: 'Postavljamo povrtnjak…' },
			'Planning raised beds…': { de: 'Hochbeete werden geplant…', hr: 'Planiramo povišene gredice…' },
			'Planning flower beds and borders…': { de: 'Blumenbeete und Rabatten werden geplant…', hr: 'Planiramo cvjetne gredice i rubove…' },
			'Choosing planting zones…': { de: 'Pflanzzonen werden gewählt…', hr: 'Biramo zone sadnje…' },
			'Siting the greenhouse…': { de: 'Der Standort des Gewächshauses wird bestimmt…', hr: 'Određujemo položaj staklenika…' },
			'Planning the cold frame…': { de: 'Das Frühbeet wird geplant…', hr: 'Planiramo hladni okvir…' },
			'Placing the garden shed…': { de: 'Das Gartenhaus wird platziert…', hr: 'Postavljamo vrtnu kućicu…' },
			'Planning storage…': { de: 'Stauraum wird geplant…', hr: 'Planiramo spremišni prostor…' },
			'Designing the pergola…': { de: 'Die Pergola wird gestaltet…', hr: 'Dizajniramo pergolu…' },
			'Planning the gazebo…': { de: 'Der Pavillon wird geplant…', hr: 'Planiramo sjenicu…' },
			'Designing the deck…': { de: 'Das Deck wird gestaltet…', hr: 'Dizajniramo deck…' },
			'Planning the patio…': { de: 'Die Terrasse wird geplant…', hr: 'Planiramo terasu…' },
			'Making the pathway…': { de: 'Der Weg wird angelegt…', hr: 'Izrađujemo stazu…' },
			'Laying the walkway…': { de: 'Der Gehweg wird verlegt…', hr: 'Postavljamo pješačku stazu…' },
			'Planning the driveway…': { de: 'Die Einfahrt wird geplant…', hr: 'Planiramo prilaz…' },
			'Laying out parking…': { de: 'Der Parkbereich wird angelegt…', hr: 'Postavljamo parkirni prostor…' },
			'Planning the retaining wall…': { de: 'Die Stützmauer wird geplant…', hr: 'Planiramo potporni zid…' },
			'Setting up the BBQ area…': { de: 'Der BBQ-Bereich wird eingerichtet…', hr: 'Postavljamo BBQ zonu…' },
			'Choosing the grill spot…': { de: 'Der Grillplatz wird gewählt…', hr: 'Biramo mjesto za roštilj…' },
			'Designing the outdoor kitchen…': { de: 'Die Außenküche wird gestaltet…', hr: 'Dizajniramo vanjsku kuhinju…' },
			'Placing the fire pit…': { de: 'Die Feuerstelle wird platziert…', hr: 'Postavljamo vatrište…' },
			'Planning the fireplace…': { de: 'Der Kamin wird geplant…', hr: 'Planiramo kamin…' },
			'Planning the pizza oven…': { de: 'Der Pizzaofen wird geplant…', hr: 'Planiramo peć za pizzu…' },
			'Choosing the furniture…': { de: 'Möbel werden ausgewählt…', hr: 'Biramo namještaj…' },
			'Planning the seating area…': { de: 'Der Sitzbereich wird geplant…', hr: 'Planiramo prostor za sjedenje…' },
			'Planning the play area…': { de: 'Der Spielbereich wird geplant…', hr: 'Planiramo zonu za igru…' },
			'Planning outdoor lighting…': { de: 'Die Außenbeleuchtung wird geplant…', hr: 'Planiramo vanjsku rasvjetu…' },
			'Placing lights…': { de: 'Lichter werden platziert…', hr: 'Postavljamo rasvjetu…' },
			'Planning the fence…': { de: 'Der Zaun wird geplant…', hr: 'Planiramo ogradu…' },
			'Choosing fence lines…': { de: 'Zaunlinien werden festgelegt…', hr: 'Određujemo linije ograde…' },
			'Placing the compost area…': { de: 'Der Kompostbereich wird platziert…', hr: 'Postavljamo kompost zonu…' },
		};
		return messages.map((m) => {
			const hit = dict[m];
			if (!hit) return m;
			return language === 'de' ? hit.de : hit.hr;
		});
	}, [messages, language]);
	const [messageIndex, setMessageIndex] = useState(0);
	// Force the puzzle grid to remount periodically so the assemble
	// animation always restarts from a clean state (all pieces hidden).
	const [puzzleLoopId, setPuzzleLoopId] = useState(0);

	// Length of one full assemble cycle in ms – a bit longer than the CSS
	// animation (~20s) so the completed puzzle holds for a moment before looping.
	const PUZZLE_LOOP_MS = 24000;

	useEffect(() => {
		if (teaserMode) return;
		if (messages.length <= 1) return;
		const id = setInterval(() => {
			setMessageIndex((i) => (i + 1) % messages.length);
		}, MESSAGE_INTERVAL_MS);
		return () => clearInterval(id);
	}, [messages.length, teaserMode]);

	useEffect(() => {
		if (teaserMode) return;
		const id = window.setInterval(() => {
			setPuzzleLoopId((loop) => loop + 1);
		}, PUZZLE_LOOP_MS);
		return () => window.clearInterval(id);
	}, [teaserMode]);

	useEffect(() => {
		if (!teaserMode || !onTeaserComplete) return;
		const id = window.setTimeout(() => onTeaserComplete(), 2400);
		return () => clearTimeout(id);
	}, [teaserMode, onTeaserComplete]);

	const resolvedImageUrl = beforeImageUrl ? getImageUrl(beforeImageUrl) : undefined;
	const showPuzzle = Boolean(resolvedImageUrl);

	if (!showPuzzle) {
		return (
			<GardenAssemblyAnimation
				message={localizedMessages[messageIndex] ?? localizedMessages[0]}
				subMessage={subMessage}
			/>
		);
	}

	return (
		<div className={`puzzle-gen-container${teaserMode ? ' puzzle-gen-teaser' : ''}`}>
			<div className="puzzle-gen-scene">
				{/* Blurred before image behind the puzzle – use img so filter: blur() and data URLs work reliably */}
				{resolvedImageUrl && (
					<img
						className="puzzle-gen-backdrop"
						src={resolvedImageUrl}
						alt=""
						aria-hidden
					/>
				)}
				<div className="puzzle-gen-grid" key={puzzleLoopId}>
					{Array.from({ length: ROWS }, (_, row) =>
						Array.from({ length: COLS }, (_, col) => {
							const i = row * COLS + col;
							/* background-position so each cell shows the right part of the image */
							const x = COLS > 1 ? `${(col / (COLS - 1)) * 100}%` : '0%';
							const y = ROWS > 1 ? `${(row / (ROWS - 1)) * 100}%` : '0%';
							return (
								<div
									key={i}
									className="puzzle-gen-piece"
									data-piece={i}
									data-row={row}
									data-col={col}
									style={{
										backgroundImage: resolvedImageUrl ? `url(${resolvedImageUrl})` : undefined,
										backgroundPosition: `${x} ${y}`,
									}}
								/>
							);
						})
					)}
				</div>
			</div>
			<div className="puzzle-gen-text">
				<div>
					<h5>{localizedMessages[messageIndex] ?? localizedMessages[0]}</h5>
					<p className="puzzle-gen-sub text-muted small mb-0">{subMessage}</p>
				</div>
			</div>
			<div className="puzzle-gen-progress">
				<div className="puzzle-gen-progress-bar" />
			</div>
		</div>
	);
}
